/**
 * Centralized logging system with configurable levels
 * In production, only warnings and errors are logged
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  minLevel: LogLevel;
  enabled: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Check if we're in production mode
const isProduction = import.meta.env.PROD;

const config: LoggerConfig = {
  minLevel: isProduction ? 'warn' : 'debug',
  enabled: true,
};

function shouldLog(level: LogLevel): boolean {
  if (!config.enabled) return false;
  return LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
}

function formatMessage(level: LogLevel, message: string, context?: string): string {
  const timestamp = new Date().toISOString();
  const prefix = context ? `[${context}]` : '';
  return `${timestamp} [${level.toUpperCase()}]${prefix} ${message}`;
}

export const logger = {
  /**
   * Debug level - only shown in development
   */
  debug(message: string, data?: unknown, context?: string) {
    if (shouldLog('debug')) {
      console.log(formatMessage('debug', message, context), data ?? '');
    }
  },

  /**
   * Info level - only shown in development
   */
  info(message: string, data?: unknown, context?: string) {
    if (shouldLog('info')) {
      console.info(formatMessage('info', message, context), data ?? '');
    }
  },

  /**
   * Warning level - shown in all environments
   */
  warn(message: string, data?: unknown, context?: string) {
    if (shouldLog('warn')) {
      console.warn(formatMessage('warn', message, context), data ?? '');
    }
  },

  /**
   * Error level - shown in all environments
   */
  error(message: string, error?: unknown, context?: string) {
    if (shouldLog('error')) {
      console.error(formatMessage('error', message, context), error ?? '');
    }
  },

  /**
   * Configure the logger
   */
  configure(options: Partial<LoggerConfig>) {
    Object.assign(config, options);
  },

  /**
   * Check if a log level would be logged
   */
  isEnabled(level: LogLevel): boolean {
    return shouldLog(level);
  },
};

export type { LogLevel, LoggerConfig };
